/*
 * The Jolly Clock-Out — nomination form (M13 Phase 1, connected in M14).
 *
 * ──────────────────────────────────────────────────────────────────────────
 * SUBMISSION IS OFF BY DEFAULT
 * ──────────────────────────────────────────────────────────────────────────
 * Real submission is opt-in per section, through the "Send real nominations"
 * checkbox in the Theme Customizer, which defaults to OFF. While it is off the
 * form validates and then reports honestly that nominations are not open yet —
 * it sends nothing. A preview, a duplicated section or a freshly installed
 * theme therefore cannot create a real Office or trigger a real email.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * THE CONTRACT (read from the backend, not assumed)
 * ──────────────────────────────────────────────────────────────────────────
 *   POST /apps/jolly-clockout/offices
 *   { nominatorName, nominatorEmail, companyName, officeSize, rulesAccepted }
 *
 * Same-origin, through the Shopify App Proxy, which supplies the HMAC. A
 * nomination carries no Office credential because there is no Office yet, so
 * the proxy signature is the only authentication and the backend rate-limits
 * per shop (60/hour) and per email (3/day).
 *
 * Responses — M31 (backend 64e41f3, app/nomination-proxy.server.ts):
 *
 *   200 { ok: true, status: "pending", code, officeSlug?, retryAfterSeconds? }
 *        code "nomination_created"    a new office; the email WAS sent
 *        code "verification_resent"   an existing unconfirmed office; a fresh
 *                                     link WAS sent
 *        code "already_participating" this address is already confirmed for an
 *                                     office; NO email was sent
 *        code "cooldown_active"       inside the five-minute resend window;
 *                                     NO email was sent, retryAfterSeconds given
 *   409 { ok: false, code: "duplicate_office" | "duplicate_email" }
 *   503 { ok: false, code: "verification_unavailable", outcome }
 *        the office stands, the cooldown was released, a retry can send
 *   400  validation rejected.
 *   429  { error: "Too many requests" } — rate limited.
 *   503  { ok: false, error: "unavailable" } — limiter or database degraded.
 *
 * Until M31 the backend answered 200 for both "accepted" and "already
 * nominated", deliberately indistinguishable so the endpoint could not be used
 * to enumerate which companies were in the campaign. M31 reverses that product
 * decision: a participant told "sent!" who then receives nothing has no way
 * forward, so the backend now discloses that an office or an address is already
 * taking part. That disclosure is the backend's choice, made explicitly in
 * nomination-proxy.server.ts; this file reports what it is told and adds
 * nothing. Neither duplicate response names an office or a person.
 *
 * TWO OF THESE OUTCOMES SEND NO EMAIL. `already_participating` and
 * `cooldown_active` both arrive as 200 with ok:true, and neither may show the
 * accepted message or set the submit button to "Nomination sent" — that is the
 * exact failure M31 exists to remove.
 *
 * `officeSlug` is returned with `already_participating` and is deliberately
 * NOT used here: reading an Office Wall needs a credential this browser may
 * not hold, and linking to a wall that answers 404 is worse than not linking.
 *
 * The Office is created as PENDING_VERIFICATION and is unreachable until the
 * nominator confirms by email, so there is nothing to redirect to and no token
 * is returned here.
 *
 * No personal data is sent to any analytics sink: the two campaign events
 * below carry no detail payload at all.
 */
(function () {
  "use strict";

  /* Validation lives here rather than in markup so errors can be grouped into a
   * summary AND tied to each field — the browser's own bubbles can do neither.
   * Each control still carries its native `required`/`type`, so assistive tech
   * announces the constraint and a visitor without JavaScript still gets the
   * browser's validation. */
  var MESSAGES = {
    name: "Please enter your name.",
    email: "Please enter a valid work email address.",
    company: "Please enter your company name.",
    office_size: "Please choose your office size.",
    terms: "Please accept the terms to continue."
  };

  /* Deliberately permissive: an address the backend will re-validate anyway, and
   * over-strict client patterns reject real addresses. */
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* Same-origin App Proxy path. Shopify appends the HMAC and the verified
   * `shop`; the theme never builds a backend hostname. */
  var NOMINATE_URL = "/apps/jolly-clockout/offices";

  function setText(el, value) {
    if (el) el.textContent = value == null ? "" : String(value);
  }

  function fieldValue(el) {
    if (!el) return "";
    return el.type === "checkbox" ? (el.checked ? "on" : "") : el.value.trim();
  }

  function isValid(key, value) {
    if (key === "email") return EMAIL.test(value);
    return value !== "";
  }

  function init(form) {
    if (form.jclReady) return;
    form.jclReady = true;

    var fields = form.querySelectorAll("[data-jcl-field]");
    var summary = form.querySelector("[data-jcl-form-summary]");
    var status = form.querySelector("[data-jcl-form-status]");
    /* M31 — the live region now holds a title and a body. Both are optional:
     * a section rendered before M31 has neither, and showStatus() falls back to
     * writing the container directly so an un-updated section still speaks. */
    var statusTitle = form.querySelector("[data-jcl-status-title]");
    var statusBody = form.querySelector("[data-jcl-status-body]");
    var submit = form.querySelector("[data-jcl-submit]");
    var submitLabel = form.querySelector("[data-jcl-submit-label]");
    var busy = false;
    var started = false;
    /* Latched on acceptance: one nomination per rendered form. */
    var done = false;

    function errorEl(key) {
      return form.querySelector('[data-jcl-error="' + key + '"]');
    }

    function showFieldError(el, key, message) {
      var target = errorEl(key);
      if (target) {
        setText(target, message);
        target.hidden = !message;
      }
      if (message) {
        el.setAttribute("aria-invalid", "true");
      } else {
        el.removeAttribute("aria-invalid");
      }
    }

    function clearSummary() {
      if (!summary) return;
      summary.textContent = "";
      summary.hidden = true;
    }

    /* A summary that lists every problem at once and takes focus, so a keyboard
     * or screen-reader user is told what went wrong without hunting field by
     * field. Each entry links to its control. */
    function showSummary(problems) {
      if (!summary || !problems.length) return;
      summary.textContent = "";

      var intro = document.createElement("p");
      intro.textContent =
        problems.length === 1
          ? "There is 1 problem with this form:"
          : "There are " + problems.length + " problems with this form:";
      summary.appendChild(intro);

      var list = document.createElement("ul");
      problems.forEach(function (problem) {
        var item = document.createElement("li");
        var link = document.createElement("a");
        link.href = "#" + problem.el.id;
        link.textContent = problem.message;
        link.addEventListener("click", function (event) {
          event.preventDefault();
          try {
            problem.el.focus({ preventScroll: false });
          } catch (e) {
            /* focus is a convenience only */
          }
        });
        item.appendChild(link);
        list.appendChild(item);
      });
      summary.appendChild(list);

      summary.hidden = false;
      summary.setAttribute("tabindex", "-1");
      try {
        summary.focus({ preventScroll: false });
      } catch (e) {
        /* focus is a convenience only */
      }
    }

    function validate() {
      var problems = [];
      for (var i = 0; i < fields.length; i++) {
        var el = fields[i];
        var key = el.getAttribute("data-jcl-field");
        var ok = isValid(key, fieldValue(el));
        showFieldError(el, key, ok ? "" : MESSAGES[key] || "");
        if (!ok) problems.push({ el: el, message: MESSAGES[key] || "" });
      }
      return problems;
    }

    /* Re-validate a field only once it has already been marked invalid, so a
     * visitor is never shown an error for something they are still typing. */
    for (var f = 0; f < fields.length; f++) {
      (function (el) {
        var key = el.getAttribute("data-jcl-field");
        var revalidate = function () {
          if (el.getAttribute("aria-invalid") !== "true") return;
          var ok = isValid(key, fieldValue(el));
          if (ok) showFieldError(el, key, "");
        };
        el.addEventListener("input", revalidate);
        el.addEventListener("change", revalidate);
        el.addEventListener("blur", function () {
          if (fieldValue(el) === "" && el.getAttribute("aria-invalid") !== "true") return;
          var ok = isValid(key, fieldValue(el));
          showFieldError(el, key, ok ? "" : MESSAGES[key] || "");
        });

        /* Tracking hook — intentionally NOT wired to any analytics in Phase 1.
         * Phase 3 attaches `nomination_start` here, once, on first interaction. */
        el.addEventListener("focus", function () {
          if (started) return;
          started = true;
          form.dispatchEvent(new CustomEvent("jcl:nomination-start", { bubbles: true }));
        });
      })(fields[f]);
    }

    function setBusy(state) {
      busy = !!state;
      if (!submit) return;
      /* aria-disabled, never `disabled`: disabling the control that holds focus
       * drops focus to <body> and takes the error context with it. */
      submit.setAttribute("aria-disabled", busy ? "true" : "false");
      /* M30 — BUSY AND SETTLED ARE NOT THE SAME STATE.
       *
       * `aria-disabled="true"` means two different things on this button: "a
       * request is in flight" and, after settleAccepted(), "this form is
       * finished". The stylesheet had only the one hook, so it painted
       * `cursor: progress` on both — which is why a completed nomination read
       * "Nomination sent" under a loading cursor.
       *
       * This attribute marks ONLY the in-flight state, and it is written here,
       * in the single function every outcome passes through on its way to
       * settling. So it clears for all nine of them — created, resent,
       * duplicate office, duplicate email, already participating, cooldown,
       * verification unavailable, rate limited and network failure — without
       * any branch having to remember to do it. */
      submit.setAttribute("data-jcl-busy", busy ? "true" : "false");
      if (!submitLabel) return;
      setText(
        submitLabel,
        busy
          ? message("sending-label", "Sending…")
          : form.getAttribute("data-jcl-submit-default") || ""
      );
    }

    function message(key, fallback) {
      return form.getAttribute("data-jcl-msg-" + key) || fallback;
    }

    /* One live region, one announcement.
     *
     * Both lines are written with textContent, and the title is hidden rather
     * than left empty so an outcome with only a body does not announce a blank
     * heading. Clearing is `showStatus("", "")`. */
    function showStatus(title, body) {
      if (!statusBody || !statusTitle) {
        setText(status, title ? title + " " + body : body);
        return;
      }
      setText(statusTitle, title || "");
      statusTitle.hidden = !title;
      setText(statusBody, body || "");
    }

    /* M31 — the five-minute resend cooldown, in words.
     *
     * `retryAfterSeconds` is whatever the backend measured, so it is coerced
     * and bounds-checked here rather than trusted: a missing, zero, negative or
     * non-finite value simply means the extra sentence is left off, and the
     * cooldown message still stands on its own. Rounded UP, because telling
     * somebody to wait "1 minute" when 90 seconds remain earns a second failed
     * attempt. Capped at an hour so a nonsense value cannot render nonsense. */
    function waitSentence(retryAfterSeconds) {
      var seconds = Number(retryAfterSeconds);
      if (!isFinite(seconds) || seconds <= 0 || seconds > 3600) return "";
      var minutes = Math.max(1, Math.ceil(seconds / 60));
      /* Section settings have no plural forms, so the singular is its own
       * string rather than "about 1 minutes". */
      var template = minutes === 1
        ? message("cooldown-wait-one", "") || message("cooldown-wait", "")
        : message("cooldown-wait", "");
      if (!template) return "";
      return String(template).replace(/\[\[\s*minutes\s*\]\]/g, function () {
        return String(minutes);
      });
    }

    function fieldByKey(key) {
      return form.querySelector('[data-jcl-field="' + key + '"]');
    }

    /* Accepted. The submit control is NOT removed and NOT `disabled` — either
     * would drop focus from the button the visitor just pressed. It stays in
     * place, permanently inert, and `done` blocks a second submission even if
     * something else dispatches submit. */
    function settleAccepted(title, body) {
      done = true;
      setBusy(false);
      submit.setAttribute("aria-disabled", "true");
      setText(submitLabel, message("sent-label", "Nomination sent"));
      showStatus(title, body);
      /* Conversion signal. No detail payload: no name, no email, no company. */
      form.dispatchEvent(new CustomEvent("jcl:nomination-success", { bubbles: true }));
    }

    /* Not accepted, and the visitor can act on it. The form stays usable.
     *
     * M31 — "usable" is the point for every one of these. A duplicate address
     * is fixed by typing a different one; a duplicate office by nominating a
     * different one; a cooldown and a failed dispatch by waiting and pressing
     * again. None of them may leave the submit button reading "Nomination
     * sent", and none of them sets `done`. */
    function settleRecoverable(key, fallback) {
      setBusy(false);
      showStatus("", message(key, fallback));
    }

    /* The same, with the title/body pair the M31 outcomes carry. */
    function settleOutcome(key, fallbackTitle, fallbackBody, extra) {
      setBusy(false);
      var body = message(key + "-body", fallbackBody);
      showStatus(message(key + "-title", fallbackTitle), extra ? body + " " + extra : body);
    }

    async function submitNomination() {
      /* The safety switch. While it is off nothing leaves the browser. */
      if (form.getAttribute("data-jcl-live") !== "true") {
        setBusy(false);
        showStatus(
          "",
          form.getAttribute("data-jcl-pending-message") ||
            "Nominations open soon. Your details have not been sent yet."
        );
        form.dispatchEvent(new CustomEvent("jcl:nomination-submit", { bubbles: true }));
        return;
      }

      /* Field keys are the theme's; the body keys are the backend's. The two
       * vocabularies are mapped here and nowhere else. The office size string
       * is sent exactly as the <option> carries it — no casing change, no
       * remapping — because the backend stores it verbatim for reporting. */
      var rules = fieldByKey("terms");

      var payload = {
        nominatorName: fieldValue(fieldByKey("name")),
        nominatorEmail: fieldValue(fieldByKey("email")),
        companyName: fieldValue(fieldByKey("company")),
        officeSize: fieldValue(fieldByKey("office_size")),
        /* A real JSON boolean, read straight off the control.
         *
         * NOT fieldValue(), which yields "on" / "" for a checkbox: the backend
         * compares with strict identity (`body.rulesAccepted !== true` in
         * app/nomination.server.ts), so the string "on" — and "true", and 1 —
         * are all rejected. A tick box that was never ticked must not become
         * acceptance through type coercion, and nor must one that was.
         *
         * Fails closed: a missing control sends false, which the backend
         * rejects, rather than silently asserting acceptance nobody gave.
         *
         * No timestamp and no rules version are sent. The backend stamps
         * `rulesAcceptedAt` and `rulesVersion` itself, from its own clock and
         * its own configuration; a browser-supplied value would be worthless
         * as an audit record. */
        rulesAccepted: !!(rules && rules.checked)
      };

      form.dispatchEvent(new CustomEvent("jcl:nomination-submit", { bubbles: true }));

      /* Announced politely while the request is in flight. The submit label
       * changes too, but a label change on the focused control is not reliably
       * announced, so the live region carries it. Only set on the live path —
       * saying "Sending" when the safety switch is off would be a lie. */
      showStatus("", message("sending-label", "Sending…"));

      var response;
      try {
        response = await fetch(NOMINATE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify(payload),
          credentials: "same-origin"
        });
      } catch (e) {
        /* Network failure. `e` is deliberately never read or logged: a fetch
         * rejection can carry the request, and the request carries the
         * nominator's name and email. Entered values are kept for a retry. */
        settleRecoverable("retry", "That did not go through. Check your connection and try again.");
        return;
      }

      /* The body is read for 200 and 409 and 503 alike, because M31 puts the
       * outcome in `code` rather than in the status line. A body that is absent
       * or unparsable is not fatal — each branch below has a status-code-only
       * fallback, so a proxy that returns an empty 503 still says something
       * true. */
      var body = null;
      try {
        body = await response.json();
      } catch (e) {
        body = null;
      }
      var code = body && typeof body === "object" && typeof body.code === "string" ? body.code : "";

      if (response.status === 200) {
        /* A NEW nomination, or a fresh link for one that was still waiting.
         * Both genuinely sent an email, so both may say so and both close the
         * form. */
        if (code === "nomination_created" || code === "") {
          /* The empty case is the pre-M31 shape, `{ok:true,status:"pending"}`
           * with no code at all. Treated as a plain acceptance because that is
           * exactly what it meant, so a theme updated ahead of the backend does
           * not report a failure that did not happen. */
          settleAccepted(
            message("created-title", ""),
            message("success", "Thanks. Check your email to confirm your nomination.")
          );
          return;
        }

        if (code === "verification_resent") {
          settleAccepted(
            message("resent-title", ""),
            message("resent-body", "We have sent your confirmation email again.")
          );
          return;
        }

        /* NO EMAIL WAS SENT for either of the next two. They arrive as 200 with
         * ok:true and must not be mistaken for acceptance: the submit button
         * keeps its normal label and the form stays usable. */
        if (code === "already_participating") {
          settleOutcome(
            "participating",
            "You've already joined the party!",
            "This address is already confirmed for an office in this contest, so we have not sent another email."
          );
          return;
        }

        if (code === "cooldown_active") {
          settleOutcome(
            "cooldown",
            "Check your inbox!",
            "You recently requested a confirmation email. Please wait before requesting another.",
            waitSentence(body && body.retryAfterSeconds)
          );
          return;
        }

        /* A 200 carrying a code this build does not know. The backend only
         * answers 200 when the nomination is in a good state, so this reports
         * success rather than inventing a failure — but it uses the neutral
         * accepted copy, not a claim about a specific outcome. */
        settleAccepted(
          message("created-title", ""),
          message("success", "Thanks. Check your email to confirm your nomination.")
        );
        return;
      }

      /* 409 — the participation rules. Neither response names an office or a
       * person; both are fixed by changing what was typed, so the form stays
       * open and nothing is cleared. */
      if (response.status === 409) {
        if (code === "duplicate_email") {
          settleOutcome(
            "dup-email",
            "You've already joined the party!",
            "This email address has already been used to nominate or back an office. You can only support one office in this contest."
          );
          return;
        }
        if (code === "duplicate_office") {
          settleOutcome(
            "dup-office",
            "Your office is already in the running!",
            "This office has already been nominated. Ask a coworker for the Office Wall link and back the existing nomination instead."
          );
          return;
        }
        settleRecoverable("invalid", "Please check your details and try again.");
        return;
      }

      if (response.status === 400) {
        settleRecoverable("invalid", "Please check your details and try again.");
        return;
      }

      if (response.status === 429) {
        settleRecoverable("rate", "Too many nominations from here just now. Please try again later.");
        return;
      }

      /* 503 with `verification_unavailable` is the one failure the backend can
       * describe precisely: the office WAS recorded, only the email did not go
       * out, and the backend released the cooldown so pressing again actually
       * sends. `outcome` is also in the body — a Klaviyo dispatch detail that
       * means nothing to a visitor and is deliberately not rendered. */
      if (response.status === 503 && code === "verification_unavailable") {
        settleOutcome(
          "dispatch",
          "We couldn't send your confirmation email.",
          "Please try again in a moment."
        );
        return;
      }

      /* Any other 503, 405, 5xx or unexpected status: the nomination's fate is
       * unknown, so this stays retryable and says nothing about the cause. */
      settleRecoverable("retry", "That did not go through. Please try again in a moment.");
    }

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      /* Two guards, not one: `busy` blocks a double tap while a request is in
       * flight, `done` blocks a second nomination after one was accepted. */
      if (busy || done) return;

      clearSummary();
      showStatus("", "");

      var problems = validate();
      if (problems.length) {
        showSummary(problems);
        return;
      }

      setBusy(true);
      submitNomination();
    });
  }

  function boot() {
    var forms = document.querySelectorAll("[data-jcl-form]");
    for (var i = 0; i < forms.length; i++) init(forms[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* The Theme Customizer re-renders a section without reloading the page. */
  document.addEventListener("shopify:section:load", boot);
})();
